import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { UserPresence } from '@/hooks/useAreaPresence';

interface PresenceIndicatorProps {
  users: UserPresence[];
  maxVisible?: number;
  size?: 'sm' | 'md';
}

export const PresenceIndicator = ({ users, maxVisible = 3, size = 'sm' }: PresenceIndicatorProps) => {
  if (users.length === 0) return null;

  const visibleUsers = users.slice(0, maxVisible);
  const remainingCount = users.length - maxVisible;

  const sizeClasses = size === 'sm' 
    ? 'h-6 w-6 text-[10px]' 
    : 'h-8 w-8 text-xs';

  return (
    <TooltipProvider>
      <div className="flex items-center -space-x-2">
        {visibleUsers.map((user) => (
          <Tooltip key={user.oderId}>
            <TooltipTrigger asChild>
              <div className="relative">
                <Avatar className={`${sizeClasses} ring-2 ring-background`}>
                  {user.avatarUrl && <AvatarImage src={user.avatarUrl} />}
                  <AvatarFallback className="bg-primary text-primary-foreground font-medium">
                    {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                {/* Online indicator */}
                <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-green-500 ring-1 ring-background" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              <p className="font-medium">{user.firstName} {user.lastName}</p>
              {user.streetName && (
                <p className="text-muted-foreground">
                  {user.streetName}
                  {user.houseNumber && ` ${user.houseNumber}`}
                </p>
              )}
            </TooltipContent>
          </Tooltip>
        ))}
        
        {remainingCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={`${sizeClasses} flex items-center justify-center rounded-full bg-muted ring-2 ring-background font-medium`}>
                +{remainingCount}
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              <p>{remainingCount} weitere Teammitglieder aktiv</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};
